/**
 * v2-native chat UI on top of the *v1* useAgentChat hook.
 *
 * Phase 3.1 — chat parity. The visual layer is 100% v2 (Bricolage,
 * acid-lime accents, glass surfaces, italic empty state, streaming
 * cursor, thinking dots). The data layer is the actual v1 hook
 * (`@console/hooks/useAgentChat` — 945 lines of dedup, edit, delete,
 * question-card answering, scheduling, etc.) vendored via Vite alias.
 *
 * v1 source is NOT modified — v2 simply imports it as a shared library.
 *
 * What v2 inherits for free:
 *   - optimistic-message dedup (the message-disappearing bug is fixed)
 *   - streaming chunk handling
 *   - awaiting-Director thinking state
 *   - error + retry
 *   - referenced_node_ids @mention plumbing (UI for it ships in 3.2)
 *
 * What's still UI-only in this iteration (deferred to 3.2):
 *   - inline question/cost-confirmation cards (the hook exposes
 *     respondToQuestions; we just don't render the card UI yet)
 *   - edit/delete message buttons (the hook exposes the methods)
 *   - attachment upload UI
 */
import { useEffect, useMemo, useRef, useState, KeyboardEvent } from 'react'
import type * as Y from 'yjs'
import {
  useAgentChat,
  type ChatMessage,
  type EmbeddedQuestion,
  type AnswerValue,
} from '@console/hooks/useAgentChat'
import { useExecutionPlan, type ExecutionPlanBatch } from '@console/hooks/useExecutionPlan'
import {
  canAutoConfirmQuestion,
  canAutoConfirmBatch,
  DEFAULT_AUTO_CONFIRM_COST_SETTINGS,
} from '@console/components/chat/autoConfirmCost'

import { AdImageUploadCard } from './ads/AdImageUploadCard'
import { AdReadyCard } from './ads/AdReadyCard'
import { AdUrlConfirmCard } from './ads/AdUrlConfirmCard'

// Tool-use prefixes that the orchestrator handles as scheduling
// (cost-confirmation, insufficient funds). User-facing question UI
// hides them; auto-confirm handles `__cost_confirmation__` for image
// generation by default (same v1 settings: image=true, video=false).
const SCHEDULING_PREFIXES = ['__cost_confirmation__', '__insufficient_funds__']
function isSchedulingQuestion(q: EmbeddedQuestion): boolean {
  return SCHEDULING_PREFIXES.some((p) => q.tool_use_id.startsWith(p))
}

// One attached upload, displayed as a chip above the textarea.
// On send, every attachment's `nodeId` is forwarded to the Director via
// `startWithPrompt(prompt, referencedNodeIds=[...])` so the agent can
// wire the uploaded image to the right downstream node (typically a
// character_designer it's about to create).
export interface ChatAttachment {
  nodeId: string
  name: string
  status: 'uploading' | 'done' | 'error'
}

export function ChatPanel({
  projectId, initialPrompt, initialReferencedNodeIds, ydoc, onAttachFile,
}: {
  projectId: string
  initialPrompt?: string
  /** Canvas nodes for files attached in the landing Composer. */
  initialReferencedNodeIds?: string[]
  ydoc?: Y.Doc
  onAttachFile?: (file: File) => Promise<ChatAttachment>
}) {
  const {
    messages,
    status,
    awaitingDirector,
    error,
    isLoading,
    startWithPrompt,
    respondToQuestions,
    stop,
  } = useAgentChat(projectId)

  // ── Auto-confirm image cost questions (matches v1 default behavior) ──
  // The Director emits `__cost_confirmation__` questions before image-
  // generation batches (character/location designers, image generator,
  // etc.). v1 auto-confirms image batches by default (video=manual).
  // Without this, those questions sit in pending_questions forever and
  // the user sees "Waiting for cost approval..." on every node.
  //
  // We extract all unanswered scheduling questions across messages,
  // check each against v1's canAutoConfirmQuestion, and respond in one
  // batched call to respondToQuestions (which v1's hook then forwards
  // to the orchestrator).
  const pendingCostQuestions = useMemo(() => {
    const out: EmbeddedQuestion[] = []
    for (const m of messages) {
      if (!m.questions) continue
      for (const q of m.questions) {
        if (!isSchedulingQuestion(q)) continue
        if (q.answer != null) continue
        if (!canAutoConfirmQuestion(q, DEFAULT_AUTO_CONFIRM_COST_SETTINGS)) continue
        out.push(q)
      }
    }
    return out
  }, [messages])
  const lastAutoConfirmKey = useRef<string>('')
  useEffect(() => {
    if (pendingCostQuestions.length === 0) return
    const key = pendingCostQuestions.map((q) => q.tool_use_id).sort().join(',')
    if (key === lastAutoConfirmKey.current) return
    lastAutoConfirmKey.current = key
    const answers: Record<string, AnswerValue> = {}
    for (const q of pendingCostQuestions) {
      answers[q.tool_use_id] = { selected_option: 'confirm', custom_text: 'cost auto confirmed' }
    }
    void respondToQuestions(answers)
  }, [pendingCostQuestions, respondToQuestions])

  // ── Newer cost-confirm path: YDoc-backed execution plan batches ──
  // The orchestrator queues batches in ydoc.getMap('execution_plan');
  // v1's ChatSidebar auto-confirms image batches the same way. Without
  // this, image-generating batches (5 characters + 3 locations = 8
  // designer nodes) sit in "Waiting for cost approval..." forever.
  const { batches: planBatches, confirmBatch, cancelBatch } = useExecutionPlan(ydoc ?? null, projectId)
  const autoConfirmedBatchKeys = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!planBatches?.length) return
    const currentKeys = new Set(planBatches.map((b) => `${projectId}:${b.batchId}`))
    // GC: drop keys for batches that have left the list
    for (const k of autoConfirmedBatchKeys.current) {
      if (!currentKeys.has(k)) autoConfirmedBatchKeys.current.delete(k)
    }
    for (const batch of planBatches) {
      if (!canAutoConfirmBatch(batch, DEFAULT_AUTO_CONFIRM_COST_SETTINGS)) continue
      const k = `${projectId}:${batch.batchId}`
      if (autoConfirmedBatchKeys.current.has(k)) continue
      autoConfirmedBatchKeys.current.add(k)
      void confirmBatch(batch.batchId).catch(() => {
        // Let user retry manually if backend rejects (e.g. insufficient
        // sparks). Remove from the dedup set so a follow-up effect cycle
        // (after a batch state change) can retry.
        autoConfirmedBatchKeys.current.delete(k)
      })
    }
  }, [planBatches, projectId, confirmBatch])

  // Manual cost approval — batches that policy refuses to auto-confirm
  // (video, audio, mixed) sit here waiting for explicit user action.
  // Without rendering a card the placeholder text "Waiting for cost
  // approval in chat..." on the video panels stays forever.
  const manualBatches = useMemo<ExecutionPlanBatch[]>(
    () =>
      (planBatches ?? []).filter(
        (b) =>
          b.status === 'pending' &&
          !canAutoConfirmBatch(b, DEFAULT_AUTO_CONFIRM_COST_SETTINGS),
      ),
    [planBatches],
  )

  const [input, setInput] = useState('')
  const [attachments, setAttachments] = useState<ChatAttachment[]>([])
  const listRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const initialSentRef = useRef(false)

  // File attach handler — optimistic chip while bytes upload.
  const handleFilePicked = async (file: File | null | undefined) => {
    if (!file || !onAttachFile) return
    // Reset the file input so picking the SAME file twice still fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = ''
    const tempId = `pending_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`
    setAttachments((cur) => [
      ...cur,
      { nodeId: tempId, name: file.name, status: 'uploading' },
    ])
    try {
      const real = await onAttachFile(file)
      setAttachments((cur) =>
        cur.map((a) => (a.nodeId === tempId ? real : a)),
      )
    } catch (err) {
      console.warn('attach failed:', err)
      setAttachments((cur) =>
        cur.map((a) =>
          a.nodeId === tempId ? { ...a, status: 'error' } : a,
        ),
      )
    }
  }
  const removeAttachment = (nodeId: string) => {
    setAttachments((cur) => cur.filter((a) => a.nodeId !== nodeId))
  }

  // Auto-send the initialPrompt from the composer once, if room is empty.
  useEffect(() => {
    if (initialSentRef.current) return
    if (!initialPrompt) return
    if (isLoading) return
    if (messages.length > 0) {
      initialSentRef.current = true
      return
    }
    initialSentRef.current = true
    void startWithPrompt(
      initialPrompt,
      initialReferencedNodeIds?.length ? initialReferencedNodeIds : undefined,
    )
  }, [initialPrompt, initialReferencedNodeIds, isLoading, messages.length, startWithPrompt])

  // Scroll to bottom on message / streaming change.
  useEffect(() => {
    const el = listRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [messages, awaitingDirector])

  // Auto-grow input.
  useEffect(() => {
    const t = textareaRef.current
    if (!t) return
    t.style.height = 'auto'
    t.style.height = Math.min(t.scrollHeight, 180) + 'px'
  }, [input])

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter sends; Shift+Enter inserts a newline. IME composition
    // (Pinyin/Kana candidate selection) presses Enter to commit — guard
    // against intercepting that mid-compose.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      doSend()
    }
  }
  async function doSend() {
    const t = input.trim()
    if (!t) return
    // Forward attached upload node IDs to the Director via the v1 hook's
    // 2nd arg. Only `done` attachments count; in-flight uploads are
    // dropped so the Director doesn't reference a node it can't read yet.
    const referencedNodeIds = attachments
      .filter((a) => a.status === 'done')
      .map((a) => a.nodeId)
    setInput('')
    setAttachments([])
    await startWithPrompt(
      t,
      referencedNodeIds.length > 0 ? referencedNodeIds : undefined,
    )
  }

  const sendDisabled = !input.trim() || awaitingDirector || status === 'running'

  return (
    <div className="flex flex-col h-full min-h-0 bg-bg-2/40 border-l border-line overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-line backdrop-blur-md bg-bg-2/60">
        <div className="flex items-center gap-2.5">
          <span className="relative inline-flex items-center justify-center w-7 h-7 rounded-md bg-gradient-to-br from-accent to-accent-soft"
                style={{ boxShadow: '0 4px 12px rgba(212,255,58,0.25), 0 0 0 1px rgba(212,255,58,0.4) inset' }}>
            <DirectorIcon />
          </span>
          <div>
            <div
              className="text-text text-[0.94rem] leading-none"
              style={{ fontVariationSettings: '"wdth" 96, "opsz" 96', fontWeight: 600, letterSpacing: '-0.018em' }}
            >
              Director
            </div>
            <div className="t-mono text-[0.66rem] text-text-3 mt-0.5">
              haevi · v2
            </div>
          </div>
        </div>
        <ConnectionPill status={status} />
      </div>

      {/* Messages — min-h-0 lets flex-1 compute a bounded height inside the
          parent flex column, so overflow-y-auto actually scrolls. */}
      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-5 py-6 space-y-5">
        {messages.length === 0 && !awaitingDirector && <EmptyState />}
        {messages.map((m) => (
          <Message
            key={m.id}
            m={m}
            onRespond={respondToQuestions}
            projectId={projectId}
          />
        ))}
        {manualBatches.map((batch) => (
          <CostApprovalCard
            key={batch.batchId}
            batch={batch}
            onConfirm={() => confirmBatch(batch.batchId)}
            onCancel={() => cancelBatch(batch.batchId)}
          />
        ))}
        {awaitingDirector && <Thinking />}
        {error && (
          <div className="rounded-md border border-err/30 bg-err/10 px-3 py-2 text-err text-[0.86rem]">
            ⨯ {error}
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="px-4 pt-3 pb-4 border-t border-line bg-bg-2/40 backdrop-blur-md">
        {/* Attachment chips — visible above the textarea when files are pending */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {attachments.map((a) => (
              <span
                key={a.nodeId}
                className="inline-flex items-center gap-1.5 rounded-full pl-2 pr-1 py-0.5 text-[0.74rem] border border-line-2 bg-bg-2/60"
                title={a.status === 'error' ? 'Upload failed' : a.name}
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{
                  background:
                    a.status === 'done' ? 'var(--ok)' :
                    a.status === 'error' ? 'var(--err)' : 'var(--accent)',
                }} />
                <span className="text-text-2 max-w-[14rem] truncate">{a.name}</span>
                <button
                  type="button"
                  onClick={() => removeAttachment(a.nodeId)}
                  className="ml-0.5 w-4 h-4 inline-flex items-center justify-center rounded-full text-text-3 hover:text-text hover:bg-bg/50 transition-colors"
                  title="Remove"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="glass rounded-md p-2 flex items-end gap-2" style={{ borderRadius: 'var(--r-md)' }}>
          {/* Paperclip — only rendered when WorkspacePage provides onAttachFile.
              Hidden file input lives next to it so picking the same file twice
              still fires onChange (the handler resets value=''). */}
          {onAttachFile && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => void handleFilePicked(e.target.files?.[0])}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md text-text-2 hover:text-text hover:bg-bg/40 transition-colors"
                title="Attach image"
              >
                <PaperclipIcon />
              </button>
            </>
          )}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask the Director… (try ‘add a chase scene to chapter 2’)"
            rows={1}
            className="flex-1 resize-none bg-transparent text-text placeholder:text-text-3 text-[0.96rem] leading-snug focus:outline-none px-2 py-2"
            style={{ minHeight: '2.4rem' }}
          />
          {(awaitingDirector || status === 'running') ? (
            <button
              onClick={() => stop()}
              className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md border border-err/40 bg-err/10 text-err hover:bg-err/20 transition-colors"
              title="Stop"
            >
              <StopIcon />
            </button>
          ) : (
            <button
              onClick={doSend}
              disabled={sendDisabled}
              className="btn-accent shrink-0"
              style={{ padding: '0.55rem 0.85rem' }}
              title="Send (⌘ + ↵)"
            >
              <SendIcon />
            </button>
          )}
        </div>
        <div className="mt-2 px-1 flex items-center justify-between text-[0.74rem] text-text-3">
          <span>⌘ + ↵ to send</span>
          <span>{messages.length} {messages.length === 1 ? 'message' : 'messages'}</span>
        </div>
      </div>
    </div>
  )
}

// ---------- Bits ----------

function Message({
  m, onRespond, projectId,
}: {
  m: ChatMessage
  onRespond: (answers: Record<string, AnswerValue>) => Promise<unknown> | void
  projectId: string
}) {
  if (m.isSystemNotification) {
    return (
      <div className="text-center">
        <div className="inline-block t-mono text-[0.7rem] uppercase tracking-wider text-text-3 px-2 py-1">
          {m.content}
        </div>
      </div>
    )
  }
  if (m.role === 'user') {
    return (
      <div className="flex justify-end">
        <div
          className="max-w-[85%] px-3.5 py-2.5 rounded-[14px] rounded-tr-[4px] text-[0.96rem] leading-relaxed whitespace-pre-wrap break-words"
          style={{
            background: 'rgba(212, 255, 58, 0.10)',
            border: '1px solid rgba(212, 255, 58, 0.22)',
            color: 'var(--text)',
          }}
        >
          {m.content}
        </div>
      </div>
    )
  }
  // Assistant
  // Skip the auto-handled scheduling questions (cost confirmation,
  // insufficient funds) — v1's hook handles those internally; we only
  // render user-facing questions.
  const visibleQuestions = (m.questions ?? []).filter((q) => !isSchedulingQuestion(q))
  return (
    <div className="flex gap-3">
      <div className="shrink-0 w-7 h-7 rounded-md bg-bg border border-line-2 flex items-center justify-center mt-0.5">
        <DirectorIcon small />
      </div>
      <div className="flex-1 min-w-0">
        <div className="t-mono text-[0.66rem] uppercase tracking-[0.18em] text-text-3 mb-1">
          {m.agent ?? 'Director'}
        </div>
        <div className="text-text text-[0.98rem] leading-relaxed whitespace-pre-wrap break-words">
          {m.content}
          {m.isStreaming && <Cursor />}
        </div>
        {visibleQuestions.length > 0 && (
          <QuestionStack
            questions={visibleQuestions}
            onRespond={onRespond}
            projectId={projectId}
          />
        )}
      </div>
    </div>
  )
}

/**
 * QuestionStack — renders Director-asked questions inline under an
 * assistant message. Each question is a card with options the user
 * clicks; an optional "custom" text input appears when the schema
 * allows it. "Submit" collects all selections and calls v1's
 * respondToQuestions (same hook v1 uses — no behavior change).
 */
export function QuestionStack({
  questions, onRespond, projectId,
}: {
  questions: EmbeddedQuestion[]
  onRespond: (answers: Record<string, AnswerValue>) => Promise<unknown> | void
  /** Required for the ``ad_image_upload`` variant to upload files via the
   *  asset endpoint. Optional so unit tests + non-ads consumers can render
   *  the stack without a projectId — the upload card degrades to URL-only
   *  entry when this is unset. */
  projectId?: string
}) {
  const [draft, setDraft] = useState<Record<string, AnswerValue>>(() => {
    // Pre-fill with any already-answered values so re-rendered messages
    // show their answered state.
    const init: Record<string, AnswerValue> = {}
    for (const q of questions) {
      if (q.answer != null) init[q.tool_use_id] = { selected_option: q.answer }
    }
    return init
  })
  const [submitting, setSubmitting] = useState(false)
  // Ref guard so React strict-mode double-invocation of setState updaters
  // (or two rapid clicks racing) can't double-fire onRespond. State alone is
  // too lagged — the second updater runs before setSubmitting(true) commits.
  const autoSubmitFiredRef = useRef(false)

  // Pending = still awaiting user input. Answered cards stay visible
  // (in readOnly state) so the user can see the choice they made — the
  // earlier "collapse to a 1-line strip after submit" treatment was a
  // regression from v1, which keeps every card on screen with the
  // selected option highlighted.
  const pending = questions.filter((q) => q.answer == null)

  function setAnswer(qid: string, selected: string, customText?: string) {
    setDraft((cur) => {
      const next = {
        ...cur,
        [qid]: {
          selected_option: selected,
          ...(customText !== undefined ? { custom_text: customText } : {}),
        },
      }
      // Custom-text edits (selected === '__custom__') are typing events
      // and must NOT trigger auto-submit — otherwise the first
      // keystroke fires a submit with one character, the timer races
      // ahead of the user's typing, and Enter-to-submit becomes a
      // no-op. The Enter key in QuestionCard calls submitCustom()
      // below instead, which fires the explicit submit path. Real
      // option clicks (selected = an option value) still auto-submit
      // once the last pending question is satisfied, mirroring v1
      // AgentChat.handleOptionSelect.
      if (selected === '__custom__') {
        return next
      }
      const willAllAnswer = pending.every((q) => {
        const a = next[q.tool_use_id]
        return a && a.selected_option && a.selected_option.length > 0
      })
      if (willAllAnswer && !submitting && !autoSubmitFiredRef.current) {
        autoSubmitFiredRef.current = true
        setTimeout(() => {
          setSubmitting(true)
          Promise.resolve(onRespond(next))
            .catch(() => undefined)
            .finally(() => setSubmitting(false))
        }, 150)
      }
      return next
    })
  }

  function submitCustom(qid: string, custom: string) {
    // Explicit submit triggered by Enter on a custom-text input. Mirrors
    // v1 AgentChat.handleCustomSubmit. Bypasses the auto-submit ref so
    // we don't have to wait for an option click to fire the request.
    if (autoSubmitFiredRef.current || submitting) return
    autoSubmitFiredRef.current = true
    setDraft((cur) => {
      const next = {
        ...cur,
        [qid]: { selected_option: '__custom__', custom_text: custom },
      }
      // Schedule with 0ms — same micro-defer as the option auto-submit
      // so React commits the draft update before onRespond reads it.
      setTimeout(() => {
        setSubmitting(true)
        Promise.resolve(onRespond(next))
          .catch(() => undefined)
          .finally(() => setSubmitting(false))
      }, 0)
      return next
    })
  }

  // No Submit button — matches v1 behavior:
  //   * Option click on the LAST unanswered question → auto-submit (setAnswer).
  //   * Custom-text answer → Enter key submits (submitCustom).
  //   * Partial answers stay in draft until the last option is chosen.
  // Cards stay visible after submit in readOnly mode so the user can see
  // what they picked (the earlier "Submit answers" button + collapsed
  // "N answered" strip were both regressions from v1).
  return (
    <div className="mt-3 space-y-3">
      {questions.map((q) => {
        const readOnly = q.answer != null || submitting
        const answer = draft[q.tool_use_id]
        // Dispatch on the variant discriminator. Default ("director")
        // is the existing options/custom-text card. Specialized variants
        // share the same setAnswer/submitCustom signals so QuestionStack's
        // auto-submit semantics stay uniform across them.
        if (q.question_type === 'ad_image_upload' && projectId) {
          return (
            <AdImageUploadCard
              key={q.tool_use_id}
              question={q}
              projectId={projectId}
              readOnly={readOnly}
              onAnswer={(path) => setAnswer(q.tool_use_id, path)}
              onCustomSubmit={(text) => submitCustom(q.tool_use_id, text)}
              customDraft={answer?.custom_text ?? ''}
            />
          )
        }
        // ad_image_upload variant requested but no projectId in this view
        // (unit tests, embedded consumers). The default QuestionCard's
        // options-list is empty for this variant, so a user would otherwise
        // see a blank card with just a text input. Surface an explicit
        // hint so the path is at least usable via URL fallback.
        if (q.question_type === 'ad_image_upload') {
          return (
            <div
              key={q.tool_use_id}
              className="rounded-md border border-line-2 bg-bg-2/60 px-3.5 py-3"
              data-testid="ad-image-upload-card-fallback"
            >
              {q.header && (
                <div className="t-mono text-[0.62rem] uppercase tracking-[0.18em] text-accent mb-1">
                  {q.header}
                </div>
              )}
              <div className="text-text text-[0.92rem] leading-snug mb-2.5">
                {q.question}
              </div>
              <div className="text-text-3 text-[0.78rem] mb-2 italic">
                Drag-and-drop upload is unavailable here — paste an asset
                path or public URL below instead.
              </div>
              <input
                type="text"
                placeholder="Asset path or URL…"
                disabled={readOnly}
                defaultValue={answer?.custom_text ?? ''}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const text = (e.target as HTMLInputElement).value.trim()
                    if (text) submitCustom(q.tool_use_id, text)
                  }
                }}
                className="field text-[0.86rem] w-full"
                style={{ padding: '0.5rem 0.7rem' }}
              />
            </div>
          )
        }
        if (q.question_type === 'ad_ready') {
          return (
            <AdReadyCard
              key={q.tool_use_id}
              question={q}
              selected={answer?.selected_option}
              readOnly={readOnly}
              onAnswer={(value) => setAnswer(q.tool_use_id, value)}
            />
          )
        }
        if (q.question_type === 'ad_url_confirm') {
          return (
            <AdUrlConfirmCard
              key={q.tool_use_id}
              question={q}
              projectId={projectId}
              selected={answer?.selected_option}
              readOnly={readOnly}
              onAnswer={(value) => {
                // Pack the scraped metadata into the answer's custom_text
                // when the user confirms. The Director's chat-result is
                // built as ``custom_text || selected_option`` (see
                // apps/api/src/.../routes/agentic/chat.py:1509), so the
                // metadata reaches the LLM's tool_result content
                // unconditionally — no backend lookup of conversation
                // history needed, no risk of context-management pruning
                // the values away. Phase D live #4/#5 surfaced both
                // failure modes: the LLM either lost the values to
                // context pruning, or "improved" the random hex
                // filename. Encoding the directive client-side
                // eliminates both.
                //
                // Phase D live #6 — the option's ``value`` field was
                // being stripped by ``EmbeddedQuestion.from_dict`` during
                // state restoration, so the frontend fell back to using
                // ``label`` and ``value`` here ended up being the
                // verbose label text. Belt-and-suspenders: match either
                // the canonical value OR the known label substring. The
                // backend ``from_dict`` was fixed in the same commit but
                // this check survives stale state files / older clients.
                const isConfirm =
                  value === 'confirm' || value.includes('Looks right')
                if (isConfirm && q.metadata) {
                  const meta = q.metadata
                  const name = meta.product_name ?? ''
                  const descriptor = meta.product_descriptor ?? ''
                  const heroPath = meta.hero_image_path ?? ''
                  if (name && descriptor && heroPath) {
                    const directive =
                      'confirm — render with these EXACT values:\n' +
                      `  product_name: ${name}\n` +
                      `  product_descriptor: ${descriptor}\n` +
                      `  product_image_path: ${heroPath}\n` +
                      'Call create_ugc_proof_ad now. Pass ' +
                      'product_image_path VERBATIM — the string above ' +
                      'is the literal path of an existing file in the ' +
                      'project asset store. Do not rename it, do not ' +
                      'change the extension, do not invent a friendlier ' +
                      'name. Any modification produces a 404.'
                    // Always submit ``confirm`` as the canonical value
                    // even when we matched on the label fallback —
                    // downstream code reads selected_option and expects
                    // the value.
                    setAnswer(q.tool_use_id, 'confirm', directive)
                    return
                  }
                }
                setAnswer(q.tool_use_id, value)
              }}
            />
          )
        }
        return (
          <QuestionCard
            key={q.tool_use_id}
            question={q}
            answer={answer}
            onAnswer={(sel, custom) => setAnswer(q.tool_use_id, sel, custom)}
            onCustomSubmit={(custom) => submitCustom(q.tool_use_id, custom)}
            readOnly={readOnly}
          />
        )
      })}
    </div>
  )
}

function QuestionCard({
  question, answer, onAnswer, onCustomSubmit, readOnly = false,
}: {
  question: EmbeddedQuestion
  answer: AnswerValue | undefined
  onAnswer: (selected: string, customText?: string) => void
  onCustomSubmit?: (custom: string) => void
  readOnly?: boolean
}) {
  const selected = answer?.selected_option
  const customAllowed = question.allow_custom_input
  return (
    <div className="rounded-md border border-line-2 bg-bg-2/60 px-3.5 py-3">
      {question.header && (
        <div className="t-mono text-[0.62rem] uppercase tracking-[0.18em] text-accent mb-1">
          {question.header}
        </div>
      )}
      <div className="text-text text-[0.92rem] leading-snug mb-2.5">
        {question.question}
      </div>
      {/* Options — full-width cards stacked vertically, matching v1.
          Each card has a bold label + muted description; selected gets
          a lime border + lime title for unambiguous selection state.
          In readOnly mode (after the answer has been submitted) the
          cards keep the selection highlight but stop responding to
          clicks — same UX as v1 AgentChat. */}
      <div className="flex flex-col gap-1.5">
        {question.options.map((opt, i) => {
          const value = opt.value ?? opt.label
          const isSelected = selected === value
          return (
            <button
              key={`${value}-${i}`}
              type="button"
              disabled={readOnly}
              onClick={readOnly ? undefined : () => onAnswer(value, answer?.custom_text)}
              className={`group text-left px-3.5 py-2.5 rounded-md border transition-[background-color,border-color,color,box-shadow] relative ${
                isSelected
                  ? 'border-accent bg-accent/[0.06] shadow-[0_0_0_1px_var(--accent)_inset]'
                  : readOnly
                    ? 'border-line-2 bg-white/[0.02] opacity-50 cursor-default'
                    : 'border-line-2 bg-white/[0.02] hover:bg-white/[0.04] hover:border-line-3'
              }`}
            >
              <div
                className={`text-[0.95rem] leading-tight ${
                  isSelected ? 'text-accent' : 'text-text'
                }`}
                style={{ fontVariationSettings: '"wdth" 96, "opsz" 96', fontWeight: 600, letterSpacing: '-0.015em' }}
              >
                {opt.label}
              </div>
              {opt.description && (
                <div className={`mt-0.5 text-[0.82rem] leading-snug ${
                  isSelected ? 'text-accent/70' : 'text-text-3'
                }`}>
                  {opt.description}
                </div>
              )}
              {isSelected && (
                <span
                  aria-hidden
                  className="absolute top-2.5 right-3 inline-flex items-center justify-center w-4 h-4 rounded-full bg-accent text-bg"
                >
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12l5 5L20 7" />
                  </svg>
                </span>
              )}
            </button>
          )
        })}
      </div>
      {customAllowed && !readOnly && (
        <div className="mt-2">
          <input
            type="text"
            placeholder="Or type a custom answer…"
            value={answer?.custom_text ?? ''}
            onChange={(e) => {
              const custom = e.target.value
              onAnswer(selected ?? '__custom__', custom)
            }}
            onKeyDown={(e) => {
              // Mirrors v1 AgentChat.handleCustomSubmit: Enter on the
              // custom field submits without needing a Submit button.
              // Uses the explicit submit path so it fires regardless of
              // whether other questions have option selections (and
              // doesn't get gated by the auto-submit ref guard the way
              // onAnswer is — typing characters one at a time would
              // otherwise race the 150ms timer).
              if (e.key === 'Enter') {
                const custom = (e.target as HTMLInputElement).value.trim()
                if (custom && onCustomSubmit) {
                  onCustomSubmit(custom)
                }
              }
            }}
            className="field text-[0.86rem]"
            style={{ padding: '0.5rem 0.7rem' }}
          />
        </div>
      )}
      {customAllowed && readOnly && answer?.custom_text && (
        <div className="mt-2 text-text-3 text-[0.82rem] italic">
          Custom: {answer.custom_text}
        </div>
      )}
    </div>
  )
}

function Thinking() {
  return (
    <div className="flex gap-3">
      <div className="shrink-0 w-7 h-7 rounded-md bg-bg border border-line-2 flex items-center justify-center">
        <DirectorIcon small />
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        <Dot delay="0ms" />
        <Dot delay="180ms" />
        <Dot delay="360ms" />
        <span className="ml-2 h-italic text-text-2 text-[0.96rem]">thinking</span>
      </div>
    </div>
  )
}

function Dot({ delay }: { delay: string }) {
  return (
    <span
      className="inline-block w-1.5 h-1.5 rounded-full bg-accent"
      style={{ animation: `live-pulse 1.4s ${delay} ease-in-out infinite` }}
    />
  )
}

function Cursor() {
  return (
    <span
      className="inline-block w-[2px] h-[1.05em] align-text-bottom ml-0.5 bg-accent"
      style={{ animation: 'live-pulse 1s ease-in-out infinite' }}
    />
  )
}

function EmptyState() {
  return (
    <div className="text-center mt-8">
      <div
        className="text-text-2 text-[1.8rem] leading-tight"
        style={{ fontFamily: 'var(--font-italic)', fontStyle: 'italic' }}
      >
        Tell me what to direct.
      </div>
      <p className="text-text-3 text-[0.92rem] mt-2 max-w-[28ch] mx-auto">
        Describe a scene, ask for a recast, or request a new chapter — I'll handle the rest.
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-1.5">
        {['Add a chase scene to chapter 2', 'Recast the protagonist', 'Make scene 3 take place at night'].map((s) => (
          <span key={s} className="pill text-[0.7rem]">{s}</span>
        ))}
      </div>
    </div>
  )
}

function ConnectionPill({ status }: { status: string }) {
  // We don't render an explicit "connecting" pill — v1's useAgentChat
  // defaults status to 'idle' even before the WS settles, and the chat
  // is interactive immediately (the user can type and send). Showing
  // a spinner here just creates a misleading "stuck" perception.
  if (status === 'running') {
    return (
      <div className="pill pill--accent">
        <span className="live-dot" />
        <span>directing</span>
      </div>
    )
  }
  if (status === 'pending_questions') {
    return (
      <div className="pill pill--coral">
        <span className="live-dot live-dot--coral" />
        <span>awaiting input</span>
      </div>
    )
  }
  if (status === 'error') {
    return (
      <div className="pill" style={{ color: 'var(--err)', borderColor: 'rgba(255,107,107,0.32)', background: 'rgba(255,107,107,0.10)' }}>
        <span>error</span>
      </div>
    )
  }
  return (
    <div className="pill pill--accent">
      <span className="live-dot" />
      <span>online</span>
    </div>
  )
}

function DirectorIcon({ small = false }: { small?: boolean }) {
  const s = small ? 12 : 14
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: small ? 'var(--text-2)' : '#0A0A05' }}>
      <polygon points="6,4 6,20 20,12" fill="currentColor" />
    </svg>
  )
}
function SendIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  )
}
function StopIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="6" width="12" height="12" rx="1.5" />
    </svg>
  )
}
function PaperclipIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  )
}

/**
 * CostApprovalCard — inline cost confirmation in the chat stream.
 *
 * Renders for any pending execution-plan batch that the auto-confirm
 * policy refuses (currently video batches; image batches auto-confirm
 * silently). Without this card a video-gen batch sits in YDoc forever
 * and the canvas panels stay at "Waiting for cost approval in chat…".
 *
 * Calls `confirmBatch` / `cancelBatch` from `useExecutionPlan`. Once
 * confirmed the orchestrator picks up the batch on its next loop and
 * fires the nodes; the batch disappears from `planBatches` and this
 * card unmounts naturally.
 */
function CostApprovalCard({
  batch,
  onConfirm,
  onCancel,
}: {
  batch: ExecutionPlanBatch
  onConfirm: () => Promise<void>
  onCancel: () => Promise<void>
}) {
  const [busy, setBusy] = useState<'confirm' | 'cancel' | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const handle = async (kind: 'confirm' | 'cancel') => {
    if (busy) return
    setBusy(kind)
    setErr(null)
    try {
      await (kind === 'confirm' ? onConfirm() : onCancel())
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
      setBusy(null)
    }
    // On success the batch leaves the list, this component unmounts.
  }

  // Collapse storyboard "batch_child" panels into a + N panels suffix on
  // the parent — same approach v1's ExecutionPlanCard takes, but the
  // simpler heuristic is fine here: video batches typically contain a
  // single homogeneous list of clips.
  const total = batch.totalSparks
  const word = total === 1 ? 'credit' : 'credits'
  const collapsed = batch.entries.length > 4
  const shown = collapsed ? batch.entries.slice(0, 3) : batch.entries
  const hiddenCount = batch.entries.length - shown.length

  return (
    <div className="rounded-md border border-line-2 bg-bg-2/60 overflow-hidden animate-fade-in">
      <div className="px-3 py-2 border-b border-line bg-bg-2/40">
        <span className="t-mono-caps text-[0.62rem]">Cost approval</span>
      </div>
      <ul className="divide-y divide-line">
        {shown.map((e) => (
          <li key={e.nodeId} className="px-3 py-2 flex items-center gap-2 text-[0.86rem]">
            <span className="flex-1 min-w-0 truncate text-text-2">{e.label || e.nodeId}</span>
            <span className="t-mono text-[0.74rem] text-text-3 shrink-0">
              {e.sparks} {e.sparks === 1 ? 'credit' : 'credits'}
            </span>
          </li>
        ))}
        {hiddenCount > 0 && (
          <li className="px-3 py-1.5 text-[0.78rem] text-text-3 italic">
            + {hiddenCount} more
          </li>
        )}
      </ul>
      <div className="px-3 py-2 border-t border-line bg-bg-2/40 flex items-center justify-between">
        <span className="text-text-2 text-[0.86rem]" style={{ fontWeight: 500 }}>Total</span>
        <span className="text-text text-[0.92rem]" style={{ fontWeight: 600, fontVariationSettings: '"wdth" 96' }}>
          {total} {word}
        </span>
      </div>
      {err && (
        <div className="px-3 py-2 border-t border-err/30 bg-err/10 text-err text-[0.78rem]">
          {err}
        </div>
      )}
      <div className="flex gap-2 p-2 border-t border-line">
        <button
          onClick={() => handle('confirm')}
          disabled={busy !== null}
          className="flex-1 btn-accent justify-center disabled:cursor-not-allowed disabled:opacity-50"
          style={{ padding: '0.55rem 0.8rem', fontSize: '0.86rem' }}
        >
          {busy === 'confirm' ? 'Approving…' : 'Approve & start'}
        </button>
        <button
          onClick={() => handle('cancel')}
          disabled={busy !== null}
          className="btn-ghost disabled:cursor-not-allowed disabled:opacity-50"
          style={{ padding: '0.55rem 0.8rem', fontSize: '0.86rem' }}
        >
          {busy === 'cancel' ? '…' : 'Cancel'}
        </button>
      </div>
    </div>
  )
}
